/**
 * Shared bid analysis formulas used by multiple templates or by shared components.
 * Template-specific formulas live in templates/NC/ncFormulas, templates/MASDirect/masDirectFormulas, templates/MASIndirect/masIndirectFormulas.
 */

import { getShortCodeFromFullName } from '../../utils/gsmsHandlingTypeUtils';

/**
 * Calculate GSMS Net Cost From Mfg (synchronous).
 * Used by useBidHandlers, useNCEffects, and recalc logic.
 */
export const calculateGSMSNetCostFromMfg = (
  gsmsCostFromMfg: number,
  supplierBulkNdc?: string,
  supplierDiscount?: number
): number => {
  if (gsmsCostFromMfg === 0) return gsmsCostFromMfg;
  if (supplierDiscount !== undefined && supplierDiscount !== null) {
    return gsmsCostFromMfg * (1 - supplierDiscount);
  }
  if (supplierBulkNdc && supplierBulkNdc.length >= 5) {
    return gsmsCostFromMfg;
  }
  return gsmsCostFromMfg;
};

/**
 * Calculate GSMS IFF Fee: Suggested bid Price * 0.005
 */
export const calculateGSMSIffFee = (suggestedBidPrice: number): number => {
  return suggestedBidPrice * 0.005;
};

/**
 * Calculate GSMS Profit (as percentage): (Suggested bid Price - GSMS Cost) / Suggested Bid Price * 100
 */
export const calculateGSMSProfit = (suggestedBidPrice: number, gsmsCost: number): number => {
  if (!suggestedBidPrice || suggestedBidPrice === 0) return 0;
  return ((suggestedBidPrice - gsmsCost) / suggestedBidPrice) * 100;
};

/**
 * Calculate Gross Profit (for Global Output): SUM(Contract Annual GP) / SUM(Contract Annual Revenue) * 100
 */
export const calculateGrossProfit = (totalRevenue: number, totalGP: number): number => {
  if (totalRevenue === 0) return 0;
  return (totalGP / totalRevenue) * 100;
};

/**
 * Calculate GP/Unit: Sum(Contract Annual GP) / Sum(Target Annual Usage)
 */
export const calculateGPPerUnit = (totalGP: number, totalTargetUsage: number): number => {
  if (totalTargetUsage === 0) return 0;
  return totalGP / totalTargetUsage;
};

/**
 * Calculate Bid Price w/o IFF: ROUND(Suggested bid price * 0.995, 2)
 */
export const calculateBidPriceWithoutIff = (suggestedBidPrice: number): number => {
  return Math.round(suggestedBidPrice * 0.995 * 100) / 100;
};

/**
 * Calculate Bid Price w/ IFF: ROUND(Bid Price w/o IFF, 2) / 0.995
 */
export const calculateBidPriceWithIff = (bidPriceWithoutIff: number | null): number | null => {
  if (bidPriceWithoutIff === null || bidPriceWithoutIff === 0) return null;
  return Math.round((bidPriceWithoutIff / 0.995) * 100) / 100;
};

/**
 * Calculate 3% WAC: WAC * 0.03
 */
export const calculate3PercentWAC = (wac: number): number => {
  return wac * 0.03;
};

/**
 * Calculate 10% PV: BID PRICE * 0.1
 */
export const calculate10PercentPV = (bidPrice: number): number => {
  return bidPrice * 0.1;
};

/**
 * Calculate 0.5% IFF: BID PRICE * 0.005
 */
export const calculate05PercentIFF = (bidPrice: number): number => {
  return bidPrice * 0.005;
};

/**
 * Calculate NET: BID PRICE - 3% WAC - 10% PV - 0.5% IFF
 */
export const calculateNet = (
  bidPrice: number,
  wac3Percent: number,
  pv10Percent: number,
  iff05Percent: number
): number => {
  return bidPrice - wac3Percent - pv10Percent - iff05Percent;
};

/**
 * Calculate TRANSFER PRICE: GSMS NET COST FROM MFG / MFG BOTTLE SIZE * PK
 */
export const calculateTransferPrice = (
  gsmsNetCostFromMfg: number,
  mfgBottleSize: number,
  gsmsPackSize: number
): number => {
  if (mfgBottleSize === 0) return 0;
  return (gsmsNetCostFromMfg / mfgBottleSize) * gsmsPackSize;
};

/**
 * Calculate UNIT BENEFIT: TRANSFER PRICE - NET
 */
export const calculateUnitBenefit = (transferPrice: number, net: number): number => {
  return transferPrice - net;
};

/**
 * Calculate ANNUAL BENEFIT: ANNUAL USAGE * UNIT BENEFIT
 */
export const calculateAnnualBenefit = (
  targetAnnualUsage: number,
  unitBenefit: number
): number => {
  return targetAnnualUsage * unitBenefit;
};

// ============================================================================
// RECALC-SHARED: Total TMS, Target Usage, Contract, FSS, Gov Savings, TMOP
// ============================================================================

/**
 * Calculate Total TMS Percent (weighted average).
 */
export const calculateTotalTmsPercent = (
  vaAnnualUsage: number,
  vaTmsPercent: number,
  dodAnnualUsage: number,
  dodTmsPercent: number
): number => {
  const totalUsage = vaAnnualUsage + dodAnnualUsage;
  if (totalUsage === 0) return 0;
  const numerator = (vaAnnualUsage * vaTmsPercent) + (dodAnnualUsage * dodTmsPercent);
  return Math.round((numerator / totalUsage) * 100) / 100;
};

/**
 * Calculate Target Annual Usage: (VA + DOD usage) * (Total TMS% / 100).
 */
export const calculateTargetAnnualUsage = (
  vaAnnualUsage: number,
  dodAnnualUsage: number,
  totalTmsPercent: number
): number => {
  return (vaAnnualUsage + dodAnnualUsage) * (totalTmsPercent / 100);
};

/**
 * Calculate Contract Annual Revenue: Target Annual Usage * Suggested Bid Price.
 */
export const calculateContractAnnualRevenue = (
  targetAnnualUsage: number,
  suggestedBidPrice: number
): number => {
  return targetAnnualUsage * suggestedBidPrice;
};

/**
 * Calculate Contract Annual GP: Contract Annual Revenue * (GSMS Profit% / 100).
 */
export const calculateContractAnnualGP = (
  contractAnnualRevenue: number,
  gsmsProfitPercent: number
): number => {
  return contractAnnualRevenue * (gsmsProfitPercent / 100);
};

/**
 * Calculate Contract Annual Cost: GSMS Cost * Target Annual Usage.
 */
export const calculateContractAnnualCost = (
  gsmsCost: number,
  targetAnnualUsage: number
): number => {
  return gsmsCost * targetAnnualUsage;
};

/**
 * Calculate Proposed New Ratio (FSS).
 */
export const calculateProposedNewRatio = (
  awardedFssRatio: number | null,
  bidPriceWithoutIff: number,
  supplierComparableWac: number,
  mfgBottleSize: number,
  gsmsPackSize: number
): number | null => {
  if (!awardedFssRatio) return null;
  const tcPrice = (supplierComparableWac / mfgBottleSize) * gsmsPackSize;
  if (tcPrice === 0) return null;
  return Math.round((bidPriceWithoutIff / tcPrice) * 100) / 100;
};

/**
 * Calculate New TC Price (FSS).
 */
export const calculateNewTcPrice = (
  awardedFssRatio: number | null,
  supplierComparableWac: number,
  mfgBottleSize: number,
  gsmsPackSize: number
): number | null => {
  if (!awardedFssRatio) return null;
  return (supplierComparableWac / mfgBottleSize) * gsmsPackSize;
};

/**
 * Calculate Max Price w/o IFF (FSS).
 */
export const calculateMaxPriceWithoutIff = (
  awardedFssRatio: number | null,
  newTcPrice: number | null
): number | null => {
  if (!awardedFssRatio || !newTcPrice) return null;
  return Math.round(awardedFssRatio * newTcPrice * 100) / 100;
};

/**
 * Calculate Max Price w/ IFF (FSS).
 */
export const calculateMaxPriceWithIff = (
  awardedFssRatio: number | null,
  maxPriceWithoutIff: number | null
): number | null => {
  if (!awardedFssRatio || !maxPriceWithoutIff) return null;
  return Math.round((maxPriceWithoutIff / 0.995) * 100) / 100;
};

/**
 * Check Ratio OK? (FSS).
 */
export const checkRatioOk = (
  awardedFssRatio: number | null,
  proposedNewRatio: number | null
): string => {
  if (!awardedFssRatio) return '';
  return proposedNewRatio === awardedFssRatio ? 'Yes' : 'No';
};

/**
 * Check Max Price OK? (FSS).
 */
export const checkMaxPriceOk = (
  awardedFssRatio: number | null,
  gsmsCost: number,
  maxPriceWithIff: number | null
): string => {
  if (!awardedFssRatio || !maxPriceWithIff) return '';
  return gsmsCost <= maxPriceWithIff ? 'Yes' : 'No';
};

/**
 * Calculate Government Savings Analysis.
 */
export const calculateGovernmentSavingsAnalysis = (
  lowestContractPrice: number,
  suggestedBidPrice: number,
  targetAnnualUsage: number
): number => {
  return (lowestContractPrice - suggestedBidPrice) * targetAnnualUsage;
};

/**
 * Calculate Annual TMOP Savings.
 */
export const calculateAnnualTmopSavings = (
  lowestContractPrice: number,
  suggestedBidPrice: number,
  vaAnnualUsage: number,
  dodAnnualUsage: number,
  tmopPercent: number
): number => {
  return (lowestContractPrice - suggestedBidPrice) * (vaAnnualUsage + dodAnnualUsage) * (tmopPercent / 100);
};

/**
 * Calculate Monthly TMOP Savings.
 */
export const calculateMonthlyTmopSavings = (annualTmopSavings: number): number => {
  return annualTmopSavings / 12;
};

/**
 * Calculate Lowest Contract PPT.
 */
export const calculateLowestContractPPT = (
  lowestContractPrice: number,
  gsmsPackSize: number
): number => {
  if (gsmsPackSize === 0) return 0;
  return lowestContractPrice / gsmsPackSize;
};

/**
 * Calculate 2nd Lowest Contract PPT.
 */
export const calculate2ndLowestContractPPT = (
  mfgBottleSize: number,
  gsmsPackSize: number
): number => {
  if (gsmsPackSize === 0) return 0;
  return mfgBottleSize / gsmsPackSize;
};

/**
 * WAC for Direct: Suggested Bid Price * 1.01.
 */
export const calculateWACForDirectBid = (suggestedBidPrice: number): number => {
  return suggestedBidPrice * 1.01;
};

/**
 * WAC for NC: Suggested Bid Price + 0.15.
 */
export const calculateWACForNC = (suggestedBidPrice: number): number => {
  return suggestedBidPrice + 0.15;
};

/**
 * GSMS Cost (object form used by NC and Direct recalc).
 */
export const calculateGSMSCost = (data: {
  gsmsNetCostFromMfg: number;
  mfgBottleSize: number;
  gsmsPackSize: number;
  gsmsBottleCost: number;
  gsmsCapCost: number;
  gsmsLabelCost: number;
  gsmsInsertCost: number;
  gsmsIffFee: number;
  gsmsBoxCartonCost: number;
  gsmsDesiccantCost: number;
  outsourceRepackFee: number;
  blendedWhlsFee: number;
  gsmsFrtCost: number;
  pv2Percent: number;
  gsmsLabor: number;
}): number => {
  const baseCost = (data.gsmsNetCostFromMfg / data.mfgBottleSize) * data.gsmsPackSize;
  const additionalCosts =
    data.gsmsBottleCost +
    data.gsmsCapCost +
    data.gsmsLabelCost +
    data.gsmsInsertCost +
    data.gsmsIffFee +
    data.gsmsBoxCartonCost +
    data.gsmsDesiccantCost +
    data.outsourceRepackFee +
    data.blendedWhlsFee +
    data.gsmsFrtCost +
    data.pv2Percent +
    data.gsmsLabor;
  return baseCost + additionalCosts;
};

/**
 * GSMS Cost (Indirect): IFF Fee + Net Cost From Mfg.
 */
export const calculateGSMSCostIndirect = (
  gsmsIffFee: number,
  gsmsNetCostFromMfg: number
): number => {
  return gsmsIffFee + gsmsNetCostFromMfg;
};

/**
 * GSMS Marketing Fee: 4-arg for Direct (with bottle capacity check), 2-arg for Indirect.
 */
export const calculateGSMSMarketingFee = (
  suggestedBidPrice: number,
  gsmsNetCostFromMfg: number,
  gsmsBottleCapacity?: string,
  isDirect?: boolean
): number => {
  if (isDirect) {
    if (!gsmsBottleCapacity || gsmsBottleCapacity.trim() === '' || gsmsBottleCapacity === ' ') {
      return suggestedBidPrice - gsmsNetCostFromMfg;
    }
    return 0;
  }
  return suggestedBidPrice - gsmsNetCostFromMfg;
};

// ---------------------------------------------------------------------------
// Shared COGS formulas — identical logic used by both NC and Direct templates.
// These are referenced by recalcCore.ts as F.calculateXxx (without isNC guard).
// ---------------------------------------------------------------------------

export const calculateGSMSCapCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.042;
    case '500cc': return 0.073;
    case '950cc': return 0.096;
    default: return 0;
  }
};

export const calculateGSMSLabelCost = (
  gsmsHandlingType: string,
  gsmsBottleCost: number
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'OL') return 0.15;
  if (gsmsBottleCost === 0) return 0;
  return 0.03;
};

export const calculateGSMSInsertCost = (
  gsmsBottleCost: number,
  description: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  if (description === 'Apotex') return 0.15;
  return 0.07;
};

export const calculateGSMSBoxCartonCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.009;
    case '500cc': return 0.039;
    case '950cc': return 0.127;
    default: return 0;
  }
};

export const calculateOutsourceRepackFee = (gsmsHandlingType: string): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  return shortCode === 'PL2' ? 0.33 : 0;
};

/** PV2% — identical formula for both NC and Direct (WAC × 2%). */
export const calculatePV2Percent = (
  gsmsBottleCapacity: string,
  wac: number
): number => {
  if (!gsmsBottleCapacity || gsmsBottleCapacity.trim() === '' || gsmsBottleCapacity === ' ') return 0;
  if (!wac || isNaN(wac) || wac <= 0) return 0;
  return wac * 0.02;
};

/** Direct-style labour formula (handling-type + suggested bid price based). */
export const calculateGSMSLabor = (
  gsmsHandlingType: string,
  suggestedBidPrice: number
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'PL' || shortCode === 'PL2') return 0.02;
  if (shortCode === 'MI') return 0;
  if (shortCode === 'OL' || shortCode === 'R') {
    if (!suggestedBidPrice || suggestedBidPrice <= 0) return 0;
    let calculatedValue = suggestedBidPrice * 0.0127;
    calculatedValue = Math.max(calculatedValue, 0.07);
    calculatedValue = Math.min(calculatedValue, 3);
    return calculatedValue;
  }
  return 0;
};
